import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import ParticleBackground from '@/components/base/ParticleBackground';
import { caseCategories, caseItems, type CaseItem } from '@/pages/case/data';

export default function Case() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('全部案例');

  const filteredCases = useMemo(
    () => (activeCategory === '全部案例' ? caseItems : caseItems.filter((c) => c.category === activeCategory)),
    [activeCategory],
  );

  const openCase = (item: CaseItem) => {
    if (item.linkType === 'external' && item.externalUrl) {
      try {
        const parsed = new URL(item.externalUrl);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      } catch {
        // Ignore invalid URLs and fallback to internal detail page.
      }
    }

    navigate(`/case/${item.slug}`);
  };

  return (
    <div className="ui-page">
      <Navbar />
      <main className="pt-20">
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background-900/40 to-transparent z-[1]"></div>
          <ParticleBackground compact />
          <div className="w-full px-4 md:px-8 lg:px-12 relative z-10">
            <div className="max-w-screen-2xl mx-auto">
              <ScrollReveal>
                <div className="text-center mb-16">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                    <span className="w-1 h-1 rounded-full bg-primary-500"></span>
                    <span className="text-primary-300 text-xs font-medium tracking-wider whitespace-nowrap">Our Works</span>
                  </div>
                  <h1 className="ui-title-glow font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-primary-500 mb-6">作品案例</h1>
                  <p className="text-secondary-400 text-lg max-w-2xl mx-auto">以AI技术驱动，打造文化数字化标杆案例</p>
                </div>
              </ScrollReveal>

              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {caseCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat
                        ? 'bg-primary-500 text-background-950 shadow-[0_0_15px_rgba(180,140,80,0.3)]'
                        : 'bg-background-800 text-secondary-400 hover:text-foreground-50 hover:bg-background-700 hover:shadow-[0_0_10px_rgba(180,140,80,0.1)]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCases.map((item) => (
                  <ScrollReveal key={item.id}>
                    <div
                      onClick={() => openCase(item)}
                      className="ui-panel group relative rounded-2xl overflow-hidden hover:border-primary-500/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_40px_rgba(180,140,80,0.15)] cursor-pointer"
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img src={item.cover} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background-900 via-transparent to-transparent"></div>
                        <div className="absolute inset-0 bg-primary-500/0 group-hover:bg-primary-500/5 transition-all duration-500"></div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="px-2.5 py-1 rounded-full bg-accent-500/15 border border-accent-500/20 text-accent-300 text-xs font-medium whitespace-nowrap group-hover:bg-accent-500/25 transition-all duration-500">{item.category}</span>
                          <span className="text-secondary-500 text-xs">{item.date}</span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground-50 mb-2 group-hover:text-primary-500 transition-colors duration-500">{item.title}</h3>
                        <p className="text-secondary-400 text-sm leading-relaxed line-clamp-2">{item.desc}</p>
                        <span className="inline-flex items-center gap-1 mt-4 text-primary-500 text-xs font-medium whitespace-nowrap group-hover:gap-2 transition-all duration-300">
                          {item.linkType === 'external' ? '跳转观看' : '查看样片'}
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-arrow-right-line"></i>
                          </div>
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary-500/0 to-transparent group-hover:via-primary-500/60 transition-all duration-500"></div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
